import pluralize from "pluralize";
import { Menu, MenuTabItem } from "~/entities/menuEntities";
import { CartLine, CartLineItem } from "~/entities/orderEntities";
import { v4 } from "uuid";
import { DiscountType, Promotion } from "~/entities/promotionEntities";
import { getProductsList } from "~/helpers/menuHelpers";
import { getTotalPrice } from "~/helpers/cartHelpers";

// Client side view of bundle deals for discovery and the deal builder. Figures here are visual cues worked out
// from menu prices; what the customer pays is always the server's price once items are in the cart.

export interface DealSlotOption {
  menuItem: MenuTabItem;
  price: number;
  soldOut: boolean;
}

export interface DealSlot {
  index: number;
  label: string;
  qty: number;
  options: DealSlotOption[];
}

export interface Deal {
  promotion: Promotion;
  code: string;
  name: string;
  description: string;
  price: number;
  slots: DealSlot[];
  // Largest saving across available picks, null when no pick saves anything
  maxSaving: number | null;
}

type BundlePromotion = Pick<
  Promotion,
  | "code"
  | "name"
  | "discountType"
  | "bundleComponents"
  | "targetPrice"
  | "menuCodes"
> & { description?: string; image?: Promotion["image"] };

const isBundle = (p: BundlePromotion) =>
  p.discountType === DiscountType.BUNDLE_PRICE &&
  p.targetPrice != null &&
  !!p.bundleComponents?.length &&
  p.bundleComponents.every((c) => c.qty >= 1 && c.productCodes.length);

const slotMax = (slot: DealSlot) =>
  Math.max(
    ...slot.options.filter((x) => !x.soldOut).map((x) => x.price),
    Number.NEGATIVE_INFINITY,
  );

// Bundle deals that can be built from the current menu, in promotion order
export const getDeals = (
  promotions: BundlePromotion[],
  menu: Menu | null | undefined,
): Deal[] => {
  if (!menu) {
    return [];
  }
  const itemsByCode = new Map(
    getProductsList(menu).map((x) => [x.productCode, x]),
  );

  const seen = new Set<string>();
  const deals: Deal[] = [];
  for (const promotion of promotions) {
    if (!isBundle(promotion) || seen.has(promotion.code)) {
      continue;
    }
    if (
      promotion.menuCodes?.length &&
      !promotion.menuCodes.includes(menu.code)
    ) {
      continue;
    }
    seen.add(promotion.code);

    const slots: DealSlot[] = promotion.bundleComponents!.map((c, index) => ({
      index,
      label: c.label || "Item",
      qty: c.qty,
      options: c.productCodes
        .map((code) => itemsByCode.get(code))
        .filter((x): x is MenuTabItem => !!x)
        .map((menuItem) => ({
          menuItem,
          price: menuItem.productData.price,
          soldOut: menuItem.productData.soldOut,
        })),
    }));

    // Every slot must be fillable from what's on this menu now
    if (slots.some((s) => !s.options.some((o) => !o.soldOut))) {
      continue;
    }

    const price = promotion.targetPrice!;
    const best = slots.reduce((acc, s) => acc + slotMax(s) * s.qty, 0);
    deals.push({
      promotion: promotion as Promotion,
      code: promotion.code,
      name: promotion.name,
      description: promotion.description ?? "",
      price,
      slots,
      maxSaving: best > price ? best - price : null,
    });
  }
  return deals;
};

// "Burger + side + drink" style summary when the promotion has no description
export const dealSummary = (deal: Deal) =>
  deal.description ||
  deal.slots
    .map((s) => (s.qty > 1 ? countOf(s.label, s.qty) : s.label))
    .join(" + ");

// "2 animals", "3 sides"
export const countOf = (label: string, n: number) =>
  `${n} ${pluralize(label.toLowerCase(), n)}`;

const withArticle = (label: string) =>
  /^[aeiou]/i.test(label) ? `an ${label}` : `a ${label}`;

const joinWords = (words: string[]) =>
  words.length <= 1
    ? words.join("")
    : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;

export interface ComboPrompt {
  deal: Deal;
  slotIndex: number;
  // What the deal adds on top of this product's price
  delta: number;
  maxSaving: number;
  // "Add a side and a drink"
  instruction: string;
}

// The deals this product can start, for the "Make it a Burger Combo" prompt in the product modal
export const getComboPrompts = (
  deals: Deal[],
  productCode: string,
): ComboPrompt[] => {
  const prompts: ComboPrompt[] = [];
  for (const deal of deals) {
    const slot = deal.slots.find((s) =>
      s.options.some(
        (o) => o.menuItem.productCode === productCode && !o.soldOut,
      ),
    );
    if (!slot) {
      continue;
    }
    const productPrice = slot.options.find(
      (o) => o.menuItem.productCode === productCode,
    )!.price;
    const best =
      productPrice +
      deal.slots.reduce(
        (acc, s) => acc + slotMax(s) * (s === slot ? s.qty - 1 : s.qty),
        0,
      );
    if (best <= deal.price) {
      continue;
    }
    const others = deal.slots
      .filter((s) => s !== slot || s.qty > 1)
      .map((s) => {
        const n = s === slot ? s.qty - 1 : s.qty;
        return n > 1 ? countOf(s.label, n) : withArticle(s.label.toLowerCase());
      });
    prompts.push({
      deal,
      slotIndex: slot.index,
      delta: deal.price - productPrice,
      maxSaving: best - deal.price,
      instruction: `Add ${joinWords(others)}`,
    });
  }
  return prompts;
};

// Product codes that belong to any deal, for the "Combo" badge on menu cards
export const dealProductCodes = (deals: Deal[]) =>
  new Set(
    deals.flatMap((d) =>
      d.slots.flatMap((s) => s.options.map((o) => o.menuItem.productCode)),
    ),
  );

// Builder picks: one qty 1 line per unit, per slot
export type DealPicks = CartLine[][];

export const emptyPicks = (deal: Deal): DealPicks => deal.slots.map(() => []);

// Assign existing lines (a deal being edited) to the deal's slots
export const picksFromLines = (deal: Deal, lines: CartLine[]): DealPicks => {
  const picks = emptyPicks(deal);
  const remaining = [...lines];
  // Most constrained slots first, so a product that fits several slots goes where it's needed
  const order = [...deal.slots].sort(
    (a, b) => a.options.length - b.options.length,
  );
  for (const slot of order) {
    for (let n = 0; n < slot.qty; n++) {
      const i = remaining.findIndex((line) =>
        slot.options.some(
          (o) => o.menuItem.productCode === line.item.productCode,
        ),
      );
      if (i < 0) {
        break;
      }
      picks[slot.index].push(remaining[i]);
      remaining.splice(i, 1);
    }
  }
  return picks;
};

export const picksComplete = (deal: Deal, picks: DealPicks) =>
  deal.slots.every((s) => picks[s.index].length === s.qty);

export const pickCount = (picks: DealPicks) =>
  picks.reduce((acc, x) => acc + x.length, 0);

export const slotsNeeded = (deal: Deal) =>
  deal.slots.reduce((acc, s) => acc + s.qty, 0);

// Running figures for the builder footer and breakdown
export const picksSummary = (deal: Deal, picks: DealPicks) => {
  const lines = picks.flat();
  const separately = lines.reduce((acc, l) => acc + getTotalPrice(l.item), 0);
  const extras = lines.reduce(
    (acc, l) => acc + getTotalPrice(l.item) - l.item.product.price * l.item.qty,
    0,
  );
  const dealTotal = deal.price + extras;
  // The server only forms the deal when it saves money, otherwise the items are charged separately
  const saving = Math.max(0, separately - dealTotal);
  return {
    extras,
    separately,
    total: saving > 0 ? dealTotal : separately,
    saving,
  };
};

export interface PartialDeal {
  deal: Deal;
  // The slot still missing one item
  slot: DealSlot;
  // Loose lines the deal would be made from (the cheapest eligible per slot, as the server does)
  countedLineIds: string[];
  // How much the order goes up by adding an item without extras
  increase: number;
  // Best saving across the items that could complete it
  saving: number;
  savingVaries: boolean;
  // Items that complete the deal and save money
  options: DealSlotOption[];
}

// A deal that loose cart lines are one item short of, for the "Add a drink to make it a Burger Combo" nudge.
// Lines already in a deal and lines with manual discounts are not considered.
export const findPartialDeal = (
  deals: Deal[],
  looseLines: CartLine[],
): PartialDeal | null => {
  let best: PartialDeal | null = null;

  for (const deal of deals) {
    const units = looseLines.flatMap((line) =>
      Array.from({ length: line.item.qty }, () => ({
        line,
        price: line.item.unitPrice,
        used: false,
      })),
    );
    const order = [...deal.slots].sort(
      (a, b) => a.options.length - b.options.length,
    );

    let counted = 0;
    const countedLineIds = new Set<string>();
    let missing: DealSlot | null = null;
    let missingCount = 0;
    for (const slot of order) {
      const codes = new Set(slot.options.map((o) => o.menuItem.productCode));
      const candidates = units
        .filter((u) => !u.used && codes.has(u.line.item.productCode))
        .sort((a, b) => a.price - b.price);
      const take = candidates.slice(0, slot.qty);
      take.forEach((u) => {
        u.used = true;
        counted += u.price;
        countedLineIds.add(u.line.id);
      });
      if (take.length < slot.qty) {
        missing = slot;
        missingCount += slot.qty - take.length;
      }
    }
    if (!missing || missingCount !== 1 || !countedLineIds.size) {
      continue;
    }

    const options = missing.options.filter(
      (o) => !o.soldOut && counted + o.price > deal.price,
    );
    if (!options.length) {
      continue;
    }
    const savings = options.map((o) => counted + o.price - deal.price);
    const saving = Math.max(...savings);
    if (!best || saving > best.saving) {
      best = {
        deal,
        slot: missing,
        countedLineIds: [...countedLineIds],
        increase: deal.price - counted,
        saving,
        savingVaries: new Set(savings).size > 1,
        options,
      };
    }
  }

  return best;
};

export const withIndefiniteArticle = (label: string) =>
  /^[aeiou]/i.test(label) ? `an ${label}` : `a ${label}`;

const withNewIds = (item: CartLineItem): CartLineItem => ({
  ...item,
  id: v4(),
  discounts: [],
  children: item.children.map(withNewIds),
});

// A fresh copy of a line (new ids, no discounts) to add to the cart as a single unit
export const copyLine = (line: CartLine): CartLine => ({
  ...line,
  id: v4(),
  discounts: [],
  item: { ...withNewIds(line.item), qty: 1 },
});
